import React, { useState, useEffect } from "react";
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography, Skeleton, } from "@mui/material";
import { Box } from "@mui/material";
import { stack2 } from "@ui/components/style/style";
import { useGetCombinedMetricsQuery } from "../../../features/projects/projectsApi";
import { useTheme } from "@mui/material/styles";
import { formatDutchNumber, formatTooltipWithLineBreaks, } from "@ui/utils/numberFormat";
import { useCompany } from "../../../context/CompanyContext";
export const ProjectTable = ({ parentProjectNumber, childProjectNumber, projectName, unitType, }) => {
    var _a, _b, _c;
    const [isLocalLoading, setIsLocalLoading] = useState(false);
    const theme = useTheme();
    const { getSelectedCompanyIds } = useCompany();
    const companyId = getSelectedCompanyIds()[0] || "";
    const { data: combinedMetrics, isLoading, error, isFetching, isUninitialized, } = useGetCombinedMetricsQuery({
        parentProjectNumber,
        childProjectNumber,
        companyId,
    }, {
        skip: !parentProjectNumber || !childProjectNumber || !companyId,
        refetchOnMountOrArgChange: true,
    });
    // Show local loading when childProjectNumber changes
    useEffect(() => {
        if (childProjectNumber) {
            setIsLocalLoading(true);
        }
    }, [childProjectNumber]);
    // Clear local loading when we have data or when query finishes
    useEffect(() => {
        if (combinedMetrics || (!isLoading && !isFetching)) {
            setIsLocalLoading(false);
        }
    }, [combinedMetrics, isLoading, isFetching]);
    const tableSx = {
        width: "100%",
    };
    // Helper function to get metric source tooltip (DB vs Calculated)
    // gridType: "first" for Totaaloverzicht, "second" for Project To Date Overzicht
    const getMetricSourceTooltip = (metricName, gridType = "first") => {
        const key = metricName.trim().toLowerCase();
        // Determine kosten tooltip based on grid type
        const kostenTooltip = gridType === "first"
            ? "Bron: Datalake\nKolomnaam: estimated_total_costs_ptd"
            : "Bron: Datalake\nKolomnaam: total_costs_ptd";
        // ProjectMarge formula differs between top grid (first) and bottom grid (second)
        const projectMargeTooltip = gridType === "first"
            ? "Van formule: (Opbrengsten - Kosten)" // Top grid: no poc multiplication
            : "Van formule: (Opbrengsten - Kosten) * poc"; // Bottom grid: with poc multiplication
        // ProjectMarge% formula differs between top grid (first) and bottom grid (second)
        const projectMargePercentTooltip = gridType === "first"
            ? "Van formule: (ProjectMarge / Opbrengsten) * 100" // Top grid: projectMarge/Opbrengsten
            : "Van formule: (Projectmarge / omzet) * 100"; // Bottom grid: (Projectmarge / omzet) * 100
        const tooltips = {
            // Direct from Datalake
            omzet: "Van formule: total_costs_ptd + projectmarge",
            opbrengsten: "Bron: Datalake\nKolomnaam: contract_ptd",
            ohw: "Van formule: (Opbrengsten - invoiced_ptd)",
            kosten: kostenTooltip,
            poc: "Van formule: (total_costs_ptd / forecast)",
            gefactureerd: "Bron: Datalake\nKolomnaam: invoiced_ptd",
            // Calculated
            projectmarge: projectMargeTooltip,
            "projectmarge%": projectMargePercentTooltip,
            "forecast margin": "Van formule: GeschatteTotaleKosten * Markup",
            "afgerond%": "Van formule: (TotaleKostenPTD / GeschatteTotaleKostenPTD) * 100",
            aanneemsom: "Van formule: OuderContract + Som(KindContracten)",
        };
        // Exact match
        if (tooltips[key])
            return formatTooltipWithLineBreaks(tooltips[key]);
        // Fuzzy matches for common variations
        if (key.includes("opbrengst"))
            return formatTooltipWithLineBreaks(tooltips.opbrengsten);
        if (key.includes("omzet"))
            return formatTooltipWithLineBreaks(tooltips.omzet);
        if (key.includes("ohw"))
            return formatTooltipWithLineBreaks(tooltips.ohw);
        if (key.includes("kosten") || key.includes("cost"))
            return formatTooltipWithLineBreaks(tooltips.kosten);
        if (key.includes("poc"))
            return formatTooltipWithLineBreaks(tooltips.poc);
        if (key.includes("gefactureerd") || key.includes("invoice"))
            return formatTooltipWithLineBreaks(tooltips.gefactureerd);
        if ((key.includes("marg") ||
            key.includes("margin") ||
            key.includes("projectmarg")) &&
            key.includes("%"))
            return formatTooltipWithLineBreaks(tooltips["projectmarge%"]);
        if (key.includes("marg") ||
            key.includes("margin") ||
            key.includes("projectmarg"))
            return formatTooltipWithLineBreaks(tooltips.projectmarge);
        if (key.includes("forecast") && key.includes("margin"))
            return formatTooltipWithLineBreaks(tooltips["forecast margin"]);
        if (key.includes("afgerond"))
            return formatTooltipWithLineBreaks(tooltips["afgerond%"]);
        if (key.includes("aanneemsom"))
            return formatTooltipWithLineBreaks(tooltips.aanneemsom);
        return "";
    };
    // Helper function to abbreviate Dutch numbers in text based on unit type
    const abbreviateNumbers = (text) => {
        // Match numbers with various formats:
        // - Dutch format with dots: "1.234.567" or "1.234.567,89"
        // - Plain numbers: "1234567" or "1234567.89"
        // - Numbers with commas: "1,234,567" or "1,234,567.89"
        return text.replace(/(-?\d{1,3}(?:[.,]\d{3})*(?:[.,]\d+)?)/g, (match) => {
            // Check if this looks like a number (has digits)
            if (!/\d/.test(match))
                return match;
            const isNegative = match.startsWith("-");
            let cleanStr = isNegative ? match.substring(1) : match;
            // Extract decimal part if present (last separator followed by digits)
            // Dutch: "1.234.567,89" -> decimal is ",89"
            // English: "1,234,567.89" -> decimal is ".89"
            let decimalPart = "";
            const decimalMatch = cleanStr.match(/[.,](\d+)$/);
            if (decimalMatch) {
                decimalPart = decimalMatch[1];
                // Remove decimal part temporarily
                cleanStr = cleanStr.substring(0, cleanStr.lastIndexOf(decimalMatch[0]));
            }
            // Remove all thousand separators (dots and commas)
            cleanStr = cleanStr.replace(/[.,]/g, "");
            // Reconstruct number string with decimal if present
            const numStr = cleanStr + (decimalPart ? "." + decimalPart : "");
            const cleanNum = parseFloat(numStr);
            if (isNaN(cleanNum))
                return match;
            const sign = isNegative ? "-" : "";
            const absNum = Math.abs(cleanNum);
            // Format based on unit type
            if (unitType === "M") {
                // Use M for millions, K for thousands
                if (absNum >= 1000000) {
                    const millions = absNum / 1000000;
                    const hasDecimals = absNum % 1000000 !== 0;
                    const formatted = hasDecimals
                        ? millions.toFixed(1).replace(/\./g, ",")
                        : Math.round(millions).toString();
                    return `${sign}${formatted}M`;
                }
                else if (absNum >= 1000) {
                    const thousands = absNum / 1000;
                    const rounded = Math.round(thousands);
                    return `${sign}${rounded}K`;
                }
            }
            else {
                // K unit type - convert to thousands without K suffix (matching main table format)
                if (absNum >= 1000) {
                    const thousands = absNum / 1000;
                    // Use formatDutchNumber for consistency with main table formatting
                    const formatted = formatDutchNumber(thousands, 0);
                    return `${sign}${formatted}`;
                }
                else {
                    // For values less than 1000, format as-is using Dutch format
                    const formatted = formatDutchNumber(absNum, 0);
                    return `${sign}${formatted}`;
                }
            }
            return match;
        });
    };
    // Helper function to format tooltip text with full or abbreviated numbers
    const formatTooltipText = (text) => {
        if (!text)
            return "";
        // Split by newline characters (\n\n for double newline, \n for single, or & character)
        // Handle both \n\n (double newline) and \n (single newline) and & delimiter
        let rawParts = [];
        if (text.includes("\n\n")) {
            // Split by double newline first (separates different entries)
            rawParts = text
                .split("\n\n")
                .map((p) => p.trim())
                .filter((p) => p.length > 0);
        }
        else if (text.includes("\n")) {
            // Split by single newline
            rawParts = text
                .split("\n")
                .map((p) => p.trim())
                .filter((p) => p.length > 0);
        }
        else if (text.includes("&")) {
            // Split by & character (legacy format)
            rawParts = text
                .split("&")
                .map((p) => p.trim())
                .filter((p) => p.length > 0);
        }
        else {
            // No delimiter found, return as single part
            rawParts = [text.trim()].filter((p) => p.length > 0);
        }
        if (rawParts.length === 0) {
            return text;
        }
        // Convert numbers to Dutch format, then abbreviate if needed
        const formattedParts = rawParts.map((part) => {
            // First convert to Dutch format
            let formatted = part.replace(/(-?\d{1,3}(?:[,.]\d{3})*(?:,\d{2})?)/g, (match) => {
                if (/,\d{2}$/.test(match)) {
                    const lastCommaIndex = match.lastIndexOf(",");
                    const beforeLastComma = match.substring(0, lastCommaIndex);
                    const afterLastComma = match.substring(lastCommaIndex + 1);
                    const integerPart = beforeLastComma.replace(/[,.]/g, ".");
                    return `${integerPart},${afterLastComma}`;
                }
                else if (match.includes(".") && match.includes(",")) {
                    const parts = match.split(".");
                    if (parts.length === 2) {
                        const integerPart = parts[0].replace(/,/g, ".");
                        return `${integerPart},${parts[1]}`;
                    }
                }
                else if (match.includes(",") && !match.includes(".")) {
                    return match.replace(/,/g, ".");
                }
                return match;
            });
            // Then abbreviate if unit type is not "full"
            if (unitType !== "full") {
                formatted = abbreviateNumbers(formatted);
            }
            return formatted;
        });
        // Return JSX with line breaks if multiple parts
        if (formattedParts.length === 1) {
            return formattedParts[0];
        }
        return (React.createElement(React.Fragment, null, formattedParts.map((part, index) => (React.createElement(React.Fragment, { key: index },
            part,
            index < formattedParts.length - 1 && React.createElement("br", null))))));
    };
    // Helper function to format numbers using Dutch convention with negative formatting
    const formatNumber = (value) => {
        if (value === 0)
            return "-";
        const absValue = Math.abs(value);
        // Format based on unit type
        let formatted;
        if (unitType === "full") {
            formatted = absValue.toLocaleString("nl-NL", {
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
            });
        }
        else if (unitType === "M") {
            // Always show in millions when M is selected
            if (absValue >= 1000000) {
                const millions = absValue / 1000000;
                const hasDecimals = absValue % 1000000 !== 0;
                formatted = formatDutchNumber(millions, hasDecimals ? 1 : 0) + "M";
            }
            else if (absValue >= 1000) {
                // Show values >= 1000 as decimal millions (e.g., 0.334M)
                const millions = absValue / 1000000;
                formatted = formatDutchNumber(millions, 2) + "M";
            }
            else {
                // Show values < 1000 as very small millions (e.g., 0.001M)
                const millions = absValue / 1000000;
                formatted = formatDutchNumber(millions, 3) + "M";
            }
        }
        else {
            // K unit type - show value divided by 1000 without K suffix
            if (absValue >= 1000) {
                const thousands = absValue / 1000;
                formatted = formatDutchNumber(thousands, 0);
            }
            else {
                formatted = formatDutchNumber(absValue, 0);
            }
        }
        if (value < 0) {
            return React.createElement("span", { style: { color: "red" } },
                "(",
                formatted,
                ")");
        }
        return formatted;
    };
    // Helper function to format percentage with negative formatting
    // toLocaleString with nl-NL already uses Dutch format (dot for thousands, comma for decimals)
    const formatPercentage = (value) => {
        if (value === 0)
            return "-";
        const absValue = Math.abs(value);
        // Format with Dutch locale which uses comma for decimals and dot for thousands
        const formatted = absValue.toLocaleString("nl-NL", {
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
        });
        const formattedWithPercent = `${formatted}%`;
        if (value < 0) {
            return React.createElement("span", { style: { color: "red" } },
                "(",
                formattedWithPercent,
                ")");
        }
        return formattedWithPercent;
    };
    // Show loader when loading, fetching, or when query is uninitialized but not skipped
    const shouldShowLoader = isLocalLoading ||
        isLoading ||
        isFetching ||
        (isUninitialized && !(!parentProjectNumber || !childProjectNumber));
    if (shouldShowLoader) {
        return (React.createElement(Box, { mt: 2, sx: Object.assign({}, stack2) },
            React.createElement(Skeleton, { variant: "text", width: 220, height: 40, sx: { mb: 2 } }),
            React.createElement(TableContainer, null,
                React.createElement(Table, { size: "small", sx: tableSx },
                    React.createElement(TableHead, null,
                        React.createElement(TableRow, null,
                            React.createElement(TableCell, { align: "left", sx: { fontWeight: 600, color: theme.palette.tertiary[30] } },
                                React.createElement(Skeleton, { variant: "text", width: 80 })),
                            React.createElement(TableCell, { align: "left", sx: { fontWeight: 600, color: theme.palette.tertiary[30] } },
                                React.createElement(Skeleton, { variant: "text", width: 60 })),
                            React.createElement(TableCell, { align: "left", sx: { fontWeight: 600, color: theme.palette.tertiary[30] } },
                                React.createElement(Skeleton, { variant: "text", width: 60 })),
                            React.createElement(TableCell, { align: "left", sx: { fontWeight: 600, color: theme.palette.tertiary[30] } },
                                React.createElement(Skeleton, { variant: "text", width: 80 })))),
                    React.createElement(TableBody, null, [...Array(8)].map((_, index) => (React.createElement(TableRow, { key: index },
                        React.createElement(TableCell, { align: "left" },
                            React.createElement(Skeleton, { variant: "text", width: 120 })),
                        React.createElement(TableCell, { align: "left" },
                            React.createElement(Skeleton, { variant: "text", width: 80 })),
                        React.createElement(TableCell, { align: "left" },
                            React.createElement(Skeleton, { variant: "text", width: 80 })),
                        React.createElement(TableCell, { align: "left" },
                            React.createElement(Skeleton, { variant: "text", width: 80 })))))))),
            React.createElement(Skeleton, { variant: "text", width: 240, height: 40, sx: { mt: 3, mb: 2 } }),
            React.createElement(TableContainer, null,
                React.createElement(Table, { size: "small", sx: tableSx },
                    React.createElement(TableHead, null,
                        React.createElement(TableRow, null,
                            React.createElement(TableCell, { align: "left", sx: { fontWeight: 600, color: theme.palette.tertiary[30] } },
                                React.createElement(Skeleton, { variant: "text", width: 80 })),
                            React.createElement(TableCell, { align: "left", sx: { fontWeight: 600, color: theme.palette.tertiary[30] } },
                                React.createElement(Skeleton, { variant: "text", width: 60 })),
                            React.createElement(TableCell, { align: "left", sx: { fontWeight: 600, color: theme.palette.tertiary[30] } },
                                React.createElement(Skeleton, { variant: "text", width: 60 })),
                            React.createElement(TableCell, { align: "left", sx: { fontWeight: 600, color: theme.palette.tertiary[30] } },
                                React.createElement(Skeleton, { variant: "text", width: 60 })),
                            React.createElement(TableCell, { align: "left", sx: { fontWeight: 600, color: theme.palette.tertiary[30] } },
                                React.createElement(Skeleton, { variant: "text", width: 60 })),
                            React.createElement(TableCell, { align: "left", sx: { fontWeight: 600, color: theme.palette.tertiary[30] } },
                                React.createElement(Skeleton, { variant: "text", width: 80 })))),
                    React.createElement(TableBody, null, [...Array(8)].map((_, index) => (React.createElement(TableRow, { key: `second-${index}` },
                        React.createElement(TableCell, { align: "left" },
                            React.createElement(Skeleton, { variant: "text", width: 120 })),
                        React.createElement(TableCell, { align: "left" },
                            React.createElement(Skeleton, { variant: "text", width: 80 })),
                        React.createElement(TableCell, { align: "left" },
                            React.createElement(Skeleton, { variant: "text", width: 80 })),
                        React.createElement(TableCell, { align: "left" },
                            React.createElement(Skeleton, { variant: "text", width: 80 })),
                        React.createElement(TableCell, { align: "left" },
                            React.createElement(Skeleton, { variant: "text", width: 80 })),
                        React.createElement(TableCell, { align: "left" },
                            React.createElement(Skeleton, { variant: "text", width: 80 }))))))))));
    }
    if (error) {
        return (React.createElement(Box, { mt: 2, sx: Object.assign(Object.assign({}, stack2), { textAlign: "center", p: 2, color: "error.main" }) }, "Fout bij het laden van projectmetrieken"));
    }
    if (!combinedMetrics) {
        return (React.createElement(Box, { mt: 2, sx: Object.assign(Object.assign({}, stack2), { textAlign: "center", p: 2, color: "text.secondary" }) }, "Geen metriekgegevens beschikbaar"));
    }
    // Helper function to get breakdown tooltip for financial metrics
    const getFinancialBreakdownTooltip = (metricName, column) => {
        if (!(combinedMetrics === null || combinedMetrics === void 0 ? void 0 : combinedMetrics.financialBreakdown))
            return "";
        const breakdown = combinedMetrics.financialBreakdown;
        const metricLower = metricName.toLowerCase();
        let tooltipText = "";
        // Check for revenue/income related terms (omzet, opbrengsten, revenue)
        if (metricLower.includes("omzet") ||
            metricLower.includes("revenue") ||
            metricLower.includes("opbrengst")) {
            if (column === "vc")
                tooltipText = breakdown.breakdownVCRevenue || "";
            if (column === "m1")
                tooltipText = breakdown.breakdownM1Revenue || "";
            if (column === "forecast")
                tooltipText = breakdown.breakdownForecastRevenue || "";
        }
        // Check for cost related terms
        else if (metricLower.includes("kosten") || metricLower.includes("costs")) {
            if (column === "vc")
                tooltipText = breakdown.breakdownVCCosts || "";
            if (column === "m1")
                tooltipText = breakdown.breakdownM1Costs || "";
            if (column === "forecast")
                tooltipText = breakdown.breakdownForecastCosts || "";
        }
        // Check for margin percentage (margin%)
        else if ((metricLower.includes("marg") || metricLower.includes("margin")) &&
            metricLower.includes("%")) {
            if (column === "vc")
                tooltipText = breakdown.breakdownVCMarginPercent || "";
            if (column === "m1")
                tooltipText = breakdown.breakdownM1MarginPercent || "";
            if (column === "forecast")
                tooltipText = breakdown.breakdownForecastMarginPercent || "";
        }
        // Check for margin related terms (margin, marg) but exclude percentage rows (margin%)
        else if ((metricLower.includes("marg") || metricLower.includes("margin")) &&
            !metricLower.includes("%")) {
            if (column === "vc")
                tooltipText = breakdown.breakdownVCMargin || "";
            if (column === "m1")
                tooltipText = breakdown.breakdownM1Margin || "";
            if (column === "forecast")
                tooltipText = breakdown.breakdownForecastMargin || "";
        }
        // Convert numbers in tooltip to Dutch format and add line breaks
        return formatTooltipText(tooltipText);
    };
    // Extract labels with fallback values (only M-1, M, and PTD use dynamic labels)
    const m1Label = (_a = combinedMetrics.m1Label) !== null && _a !== void 0 ? _a : "M1";
    const mLabel = (_b = combinedMetrics.mLabel) !== null && _b !== void 0 ? _b : "M";
    const ptdLabel = (_c = combinedMetrics.ptdLabel) !== null && _c !== void 0 ? _c : "PTD";
    return (React.createElement(Box, { mt: 2, sx: Object.assign({}, stack2) },
        React.createElement(Typography, { variant: "h5", fontWeight: 500 }, "Totaaloverzicht"),
        React.createElement(TableContainer, null,
            React.createElement(Table, { size: "small", sx: tableSx },
                React.createElement(TableHead, null,
                    React.createElement(TableRow, null,
                        React.createElement(TableCell, { align: "left", sx: { fontWeight: 500, color: theme.palette.tertiary[30] } }, "Metriek"),
                        React.createElement(TableCell, { align: "left", sx: { fontWeight: 500, color: theme.palette.tertiary[30] } }, "Voorcalculatie"),
                        React.createElement(TableCell, { align: "left", sx: { fontWeight: 500, color: theme.palette.tertiary[30] } }, "Forecast maand -1"),
                        React.createElement(TableCell, { align: "left", sx: { fontWeight: 500, color: theme.palette.tertiary[30] } }, "Forecast"))),
                React.createElement(TableBody, null, [...combinedMetrics.financials]
                    .sort((a, b) => a.sortOrder - b.sortOrder)
                    .map((metric, index) => {
                    const metricSourceTooltip = getMetricSourceTooltip(metric.metric, "first");
                    return (React.createElement(TableRow, { key: index },
                        React.createElement(TableCell, { align: "left" },
                            React.createElement(Tooltip, { title: metricSourceTooltip || "", arrow: true, placement: "top", disableHoverListener: !metricSourceTooltip },
                                React.createElement(Box, { component: "span", sx: {
                                        cursor: metricSourceTooltip ? "help" : "default",
                                    } }, metric.metric))),
                        React.createElement(Tooltip, { title: getFinancialBreakdownTooltip(metric.metric, "vc"), arrow: true, placement: "left-start" },
                            React.createElement(TableCell, { align: "left", sx: { cursor: "default" } }, metric.metric.toLowerCase().includes("%")
                                ? formatPercentage(metric.vc)
                                : formatNumber(metric.vc))),
                        React.createElement(Tooltip, { title: getFinancialBreakdownTooltip(metric.metric, "m1"), arrow: true, placement: "left-start" },
                            React.createElement(TableCell, { align: "left", sx: { cursor: "default" } }, metric.metric.toLowerCase().includes("%")
                                ? formatPercentage(metric.m1)
                                : formatNumber(metric.m1))),
                        React.createElement(Tooltip, { title: getFinancialBreakdownTooltip(metric.metric, "forecast"), arrow: true, placement: "left-start" },
                            React.createElement(TableCell, { align: "left", sx: { cursor: "default" } }, metric.metric.toLowerCase().includes("%")
                                ? formatPercentage(metric.forecast)
                                : formatNumber(metric.forecast)))));
                })))),
        React.createElement(Typography, { variant: "h5", fontWeight: 500 }, "Project To Date Overzicht"),
        React.createElement(TableContainer, null,
            React.createElement(Table, { size: "small", sx: tableSx },
                React.createElement(TableHead, null,
                    React.createElement(TableRow, null,
                        React.createElement(TableCell, { align: "left", sx: { fontWeight: 500, color: theme.palette.tertiary[30] } }),
                        React.createElement(TableCell, { align: "left", sx: { fontWeight: 500, color: theme.palette.tertiary[30] } }, m1Label),
                        React.createElement(TableCell, { align: "left", sx: { fontWeight: 500, color: theme.palette.tertiary[30] } }, mLabel),
                        React.createElement(TableCell, { align: "left", sx: { fontWeight: 500, color: theme.palette.tertiary[30] } }, ptdLabel),
                        React.createElement(TableCell, { align: "left", sx: { fontWeight: 500, color: theme.palette.tertiary[30] } }, "Te gaan"),
                        React.createElement(TableCell, { align: "left", sx: { fontWeight: 500, color: theme.palette.tertiary[30] } }, "Forecast"))),
                React.createElement(TableBody, null, [...combinedMetrics.additionalMetrics]
                    .sort((a, b) => a.sortOrder - b.sortOrder)
                    .map((metric, index) => {
                    const metricSourceTooltip = getMetricSourceTooltip(metric.metric, "second");
                    const metricLower = metric.metric.toLowerCase();
                    const breakdown = combinedMetrics.additionalBreakdown;
                    // Determine which breakdown to use based on metric name
                    let breakdownM1 = "", breakdownM = "", breakdownPTD = "", breakdownTogan = "", breakdownForecast = "";
                    if (breakdown) {
                        // Check for cost related terms
                        if (metricLower.includes("kosten") ||
                            metricLower.includes("costs")) {
                            breakdownM1 = breakdown.breakdown_Kosten_M1 || "";
                            breakdownM = breakdown.breakdown_Kosten_M || "";
                            breakdownPTD = breakdown.breakdown_Kosten_PTD || "";
                            breakdownTogan = breakdown.breakdown_Kosten_Togan || "";
                            breakdownForecast =
                                breakdown.breakdown_Kosten_Forecast || "";
                        }
                        // Check for revenue/income related terms (omzet, opbrengsten, revenue)
                        else if (metricLower.includes("omzet") ||
                            metricLower.includes("revenue") ||
                            metricLower.includes("opbrengst")) {
                            breakdownM1 = breakdown.breakdown_Omzet_M1 || "";
                            breakdownM = breakdown.breakdown_Omzet_M || "";
                            breakdownPTD = breakdown.breakdown_Omzet_PTD || "";
                            breakdownTogan = breakdown.breakdown_Omzet_Togan || "";
                            breakdownForecast =
                                breakdown.breakdown_Omzet_Forecast || "";
                        }
                        // Check for margin percentage (margin% or Projectmarge %)
                        else if ((metricLower.includes("marg") ||
                            metricLower.includes("margin") ||
                            metricLower.includes("projectmarge")) &&
                            (metricLower.includes("%") ||
                                metricLower.includes("percent"))) {
                            breakdownM1 = breakdown.breakdown_MargPercent_M1 || "";
                            breakdownM = breakdown.breakdown_MargPercent_M || "";
                            breakdownPTD = breakdown.breakdown_MargPercent_PTD || "";
                            breakdownTogan =
                                breakdown.breakdown_MargPercent_Togan || "";
                            breakdownForecast =
                                breakdown.breakdown_MargPercent_Forecast || "";
                        }
                        // Check for margin related terms (margin, marg) but exclude percentage rows (margin%)
                        else if ((metricLower.includes("marg") ||
                            metricLower.includes("margin")) &&
                            !metricLower.includes("%")) {
                            breakdownM1 = breakdown.breakdown_Marg_M1 || "";
                            breakdownM = breakdown.breakdown_Marg_M || "";
                            breakdownPTD = breakdown.breakdown_Marg_PTD || "";
                            breakdownTogan = breakdown.breakdown_Marg_Togan || "";
                            breakdownForecast = breakdown.breakdown_Marg_Forecast || "";
                        }
                        // Check for POC related terms
                        else if (metricLower.includes("poc")) {
                            breakdownM1 = breakdown.breakdown_POC_M1 || "";
                            breakdownM = breakdown.breakdown_POC_M || "";
                            breakdownPTD = breakdown.breakdown_POC_PTD || "";
                            breakdownTogan = breakdown.breakdown_POC_Togan || "";
                            breakdownForecast = breakdown.breakdown_POC_Forecast || "";
                        }
                        // Check for OHW related terms
                        else if (metricLower.includes("ohw")) {
                            breakdownM1 = breakdown.breakdown_OHW_M1 || "";
                            breakdownM = breakdown.breakdown_OHW_M || "";
                            breakdownPTD = breakdown.breakdown_OHW_PTD || "";
                            breakdownTogan = breakdown.breakdown_OHW_Togan || "";
                            breakdownForecast = breakdown.breakdown_OHW_Forecast || "";
                        }
                        // Check for Invoice/Gefactureerd related terms
                        else if (metricLower.includes("gefactureerd") ||
                            metricLower.includes("invoice")) {
                            breakdownM1 = breakdown.breakdown_Invoice_M1 || "";
                            breakdownM = breakdown.breakdown_Invoice_M || "";
                            breakdownPTD = breakdown.breakdown_Invoice_PTD || "";
                            breakdownTogan = breakdown.breakdown_Invoice_Togan || "";
                            breakdownForecast =
                                breakdown.breakdown_Invoice_Forecast || "";
                        }
                    }
                    // Check if we should display N/A for POC, Margin, Omzet, or OHW metrics
                    // Show N/A if either pocM1Display or marginM1Display is false
                    const shouldShowNA_M1 = combinedMetrics.pocM1Display === false ||
                        combinedMetrics.marginM1Display === false;
                    const shouldShowNA_M = combinedMetrics.pocM1Display === false ||
                        combinedMetrics.marginM1Display === false;
                    // Only apply N/A logic to POC, Margin, Omzet, and OHW metrics
                    const isControlledMetric = metricLower.includes("poc") ||
                        metricLower.includes("marg") ||
                        metricLower.includes("margin") ||
                        metricLower.includes("omzet") ||
                        metricLower.includes("revenue") ||
                        metricLower.includes("opbrengst") ||
                        metricLower.includes("ohw");
                    return (React.createElement(TableRow, { key: index },
                        React.createElement(TableCell, { align: "left" },
                            React.createElement(Tooltip, { title: metricSourceTooltip || "", arrow: true, placement: "top", disableHoverListener: !metricSourceTooltip },
                                React.createElement(Box, { component: "span", sx: {
                                        cursor: metricSourceTooltip ? "help" : "default",
                                    } }, metric.metric))),
                        React.createElement(Tooltip, { title: formatTooltipText(breakdownM1), arrow: true, placement: "left-start", disableHoverListener: !breakdownM1 },
                            React.createElement(TableCell, { align: "left", sx: { cursor: breakdownM1 ? "help" : "default" } }, isControlledMetric && shouldShowNA_M1
                                ? "N/A"
                                : metric.metric.toLowerCase().includes("%")
                                    ? formatPercentage(metric.m1)
                                    : formatNumber(metric.m1))),
                        React.createElement(Tooltip, { title: formatTooltipText(breakdownM), arrow: true, placement: "left-start", disableHoverListener: !breakdownM },
                            React.createElement(TableCell, { align: "left", sx: { cursor: breakdownM ? "help" : "default" } }, isControlledMetric && shouldShowNA_M
                                ? "N/A"
                                : metric.metric.toLowerCase().includes("%")
                                    ? formatPercentage(metric.m)
                                    : formatNumber(metric.m))),
                        React.createElement(Tooltip, { title: formatTooltipText(breakdownPTD), arrow: true, placement: "left-start", disableHoverListener: !breakdownPTD },
                            React.createElement(TableCell, { align: "left", sx: { cursor: breakdownPTD ? "help" : "default" } }, metric.metric.toLowerCase().includes("%")
                                ? formatPercentage(metric.ptd)
                                : formatNumber(metric.ptd))),
                        React.createElement(Tooltip, { title: formatTooltipText(breakdownTogan), arrow: true, placement: "left-start", disableHoverListener: !breakdownTogan },
                            React.createElement(TableCell, { align: "left", sx: { cursor: breakdownTogan ? "help" : "default" } }, metric.metric.toLowerCase().includes("%")
                                ? formatPercentage(metric.togan)
                                : formatNumber(metric.togan))),
                        React.createElement(Tooltip, { title: formatTooltipText(breakdownForecast), arrow: true, placement: "left-start", disableHoverListener: !breakdownForecast },
                            React.createElement(TableCell, { align: "left", sx: { cursor: breakdownForecast ? "help" : "default" } }, metric.metric.toLowerCase().includes("%")
                                ? formatPercentage(metric.forecast)
                                : formatNumber(metric.forecast)))));
                }))))));
};
